import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    signal,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import {
    IChecklistRemindTimeData,
    IChecklistTimeInterval,
    IChecklistTimeIntervalGroup,
} from '@fi-shared/checklist/checklist-v2';
import { FiChecklistScheduleType } from '../../fi-checklist-schedule-type';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { FiChecklist2ScheduleFactoryService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-factory.service';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import {
    FiChecklist2ScheduleTimeIntervalHelperService,
    IChecklistTimeIntervalViewData,
} from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-time-interval-helper.service';
import { interval, Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { IChecklistTimeIntervalIdentifier } from '@fi-shared/checklist/schedule/schedule-edit/schedule-time/time-intervals/time-intervals.component';
import { FiChecklist2ScheduleDialogService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-dialog.service';
import { FiFormErrorDisplayContextService } from '@fi-shared/fi-shared-services/fi-form-error-display-context.service';

export interface IChecklist2ScheduleEditTimeIntervalDialogData {
    scheduleType: FiChecklistScheduleType;
    timeIntervalType: FiChecklistTimeIntervalType;
    timeIntervalGroup: IChecklistTimeIntervalGroup;
    remindTime: IChecklistRemindTimeData;
    hasAnswers: boolean;
    auditMode: boolean;
}

@Component({
    selector: 'fi-checklist2-schedule-edit-time-intervals-dialog',
    templateUrl: './time-intervals-dialog.component.html',
    styleUrls: ['./time-intervals-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-schedule-edit-time-intervals-dialog' },
    standalone: false,
    providers: [FiFormErrorDisplayContextService],
})
export class FiChecklist2ScheduleEditTimeIntervalsDialogComponent
    extends FiDialogsContentBase<
        IChecklist2ScheduleEditTimeIntervalDialogData,
        IChecklist2ScheduleEditTimeIntervalDialogData
    >
    implements OnInit, OnDestroy
{
    @ViewChild(FiValidatingFormDirective) editTimeIntervalsForm: FiValidatingFormDirective;

    private fiChecklist2ScheduleFactoryService = inject(FiChecklist2ScheduleFactoryService);
    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);
    private fiChecklist2ScheduleTimeIntervalHelperService = inject(FiChecklist2ScheduleTimeIntervalHelperService);
    private fiChecklistHelperService = inject(FiChecklistHelperService);
    private fiChecklist2ScheduleDialogService = inject(FiChecklist2ScheduleDialogService);
    private fiDialogsService = inject(FiDialogsService);
    private cdr = inject(ChangeDetectorRef);
    private fiFormErrorDisplayContextService = inject(FiFormErrorDisplayContextService);

    private fiValuesComparison: FiValuesComparison<IChecklist2ScheduleEditTimeIntervalDialogData>;

    protected intervalSettings: IChecklist2ScheduleEditTimeIntervalDialogData;
    protected invalidTimeIntervalIdentifier = signal<IChecklistTimeIntervalIdentifier | undefined>(undefined);

    private _setShortestTimeInterval$ = new Subject<void>();
    protected shortestTimeInterval = signal<IChecklistTimeIntervalViewData | undefined>(undefined);
    protected isOnlyUnsetTimeIntervalError = signal<boolean>(false);

    public ngOnInit(): void {
        this.cloneDeepDialogData();
        this.subscribeOnSetShortestTimeInterval();
        this._setShortestTimeInterval$.next();
        this.fiValuesComparison = new FiValuesComparison(() => this.intervalSettings);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    private subscribeOnSetShortestTimeInterval(): void {
        this._setShortestTimeInterval$
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.setShortestTimeInterval());
    }

    private cloneDeepDialogData(): void {
        this.intervalSettings = cloneDeep(this.data);
    }

    protected checklistScheduleTypeChanged(): void {
        this.intervalSettings.timeIntervalType = this.fiChecklist2ScheduleFactoryService.getIntervalTypeByScheduleType(
            this.intervalSettings.scheduleType,
        );
        this.checklistTimeIntervalTypeChanged();
    }

    protected checklistTimeIntervalTypeChanged(): void {
        const defaultIntervals = this.fiChecklist2ScheduleIntervalService.getDefaultTimeIntervals(
            this.intervalSettings.timeIntervalType,
        );
        this.intervalSettings.timeIntervalGroup = this.fiChecklist2ScheduleIntervalService.groupTimeIntervals(
            defaultIntervals,
            this.intervalSettings.timeIntervalType,
        );
        this.onTimeIntervalsChanged();
    }

    protected isRecurringChecklistType(): boolean {
        return this.fiChecklist2ScheduleFactoryService.isRecurringChecklistSchedule(this.intervalSettings.scheduleType);
    }

    private resetRemindTime(): void {
        this.intervalSettings.remindTime.Month =
            this.intervalSettings.remindTime.Day =
            this.intervalSettings.remindTime.Hour =
            this.intervalSettings.remindTime.Minute =
                0;
    }

    protected onTimeIntervalsChanged(): void {
        this.editTimeIntervalsForm?.updateValidity();
        this._setShortestTimeInterval$.next();
        this.clearInvalidTimeIntervalIdentifier();

        this.resetRemindTime();
    }

    private setShortestTimeInterval(): void {
        let timeIntervals: IChecklistTimeInterval[] = [];

        for (let key in this.intervalSettings.timeIntervalGroup) {
            timeIntervals.push(...this.intervalSettings.timeIntervalGroup[key].filter((item) => item.IsEnabled));
        }

        const shortestTimeInterval: IChecklistTimeIntervalViewData =
            this.fiChecklist2ScheduleTimeIntervalHelperService.getShortestTimeInterval(
                timeIntervals,
                this.intervalSettings.timeIntervalType,
            );

        if (
            this.fiChecklist2ScheduleTimeIntervalHelperService.isRemindTimeDataFilledAndInvalid(
                shortestTimeInterval,
                this.intervalSettings.remindTime,
            )
        ) {
            this.resetRemindTime();
        }

        this.shortestTimeInterval.set(shortestTimeInterval);
        this.editTimeIntervalsForm?.updateValidity();
        this.cdr.markForCheck();
    }

    public getResult(): IChecklist2ScheduleEditTimeIntervalDialogData {
        return this.intervalSettings;
    }

    private isAtLeastOneIntervalTimeDataFilled(): boolean {
        return this.fiChecklist2ScheduleIntervalService.isAtLeastOneIntervalTimeDataFilled(
            this.intervalSettings.timeIntervalGroup,
        );
    }

    public isYesButtonDisabled(): boolean {
        if (
            this.isAtLeastOneIntervalTimeDataFilled() &&
            !this.fiFormErrorDisplayContextService.showValidationErrors()
        ) {
            return false;
        }
        return !!this.editTimeIntervalsForm.errors || this.editTimeIntervalsForm.pending;
    }

    public onYesButtonClick($event: MouseEvent): void {
        this.fiFormErrorDisplayContextService.show();

        const invalidTimeIntervalIdentifier = this.getInvalidTimeIntervalIdentifier();

        if (!invalidTimeIntervalIdentifier && !isDefined(this.editTimeIntervalsForm.errors)) {
            super.onYesButtonClick($event);
        } else {
            this.editTimeIntervalsForm.submit();
            $event.preventDefault();
            this.invalidTimeIntervalIdentifier.set(invalidTimeIntervalIdentifier);
            this.showRangesErrorDialog();
            this.cdr.markForCheck();
        }
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    private showRangesErrorDialog(): void {
        const intervals = this.fiChecklist2ScheduleIntervalService.getIntervalsFromGroup(
            this.intervalSettings.timeIntervalGroup,
        );
        if (
            this.fiChecklist2ScheduleIntervalService.someIntervalsConflicted(
                intervals,
                this.intervalSettings.timeIntervalType,
            ) ||
            this.fiChecklist2ScheduleIntervalService.someTimeIntervalsConflictedWithPreviousDailyGroup(
                this.intervalSettings.timeIntervalGroup,
                this.intervalSettings.timeIntervalType,
            )
        ) {
            this.fiChecklist2ScheduleDialogService.openRangesErrorDialog();
        }
    }

    private clearInvalidTimeIntervalIdentifier(): void {
        if (!this.invalidTimeIntervalIdentifier()) {
            return;
        }

        this.invalidTimeIntervalIdentifier.set(undefined);
        this.cdr.markForCheck();
    }

    private getInvalidTimeIntervalIdentifier(): IChecklistTimeIntervalIdentifier | undefined {
        if (!this.isRecurringChecklistType()) {
            return undefined;
        }

        if (this.fiChecklist2ScheduleIntervalService.hasExtraIntervals(this.intervalSettings.timeIntervalType)) {
            for (const key of Object.keys(this.intervalSettings.timeIntervalGroup)) {
                const { conflictedInterval, unsetInterval } =
                    this.fiChecklist2ScheduleIntervalService.getConflictedAndUnsetTimeIntervals(
                        this.intervalSettings.timeIntervalGroup[key],
                        this.intervalSettings.timeIntervalType,
                    );

                this.isOnlyUnsetTimeIntervalError.set(unsetInterval && !conflictedInterval);

                if (unsetInterval || conflictedInterval) {
                    return this.getTimeIntervalIdentifier(conflictedInterval ?? unsetInterval);
                }

                const conflictedWithPreviousGroupInterval =
                    this.fiChecklist2ScheduleIntervalService.getFirstTimeIntervalConflictedWithPreviousDailyGroup(
                        this.intervalSettings.timeIntervalGroup,
                        key,
                        this.intervalSettings.timeIntervalType,
                    );

                if (conflictedWithPreviousGroupInterval) {
                    this.isOnlyUnsetTimeIntervalError.set(false);
                    return this.getTimeIntervalIdentifier(conflictedWithPreviousGroupInterval);
                }
            }

            return undefined;
        }

        const { conflictedInterval, unsetInterval } =
            this.fiChecklist2ScheduleIntervalService.getConflictedAndUnsetTimeIntervals(
                this.fiChecklist2ScheduleIntervalService.getIntervalsFromGroup(this.intervalSettings.timeIntervalGroup),
                this.intervalSettings.timeIntervalType,
            );
        this.isOnlyUnsetTimeIntervalError.set(unsetInterval && !conflictedInterval);

        return conflictedInterval || unsetInterval
            ? this.getTimeIntervalIdentifier(conflictedInterval ?? unsetInterval)
            : undefined;
    }

    private getTimeIntervalIdentifier(interval: IChecklistTimeInterval): IChecklistTimeIntervalIdentifier | undefined {
        for (const scheduleTimeIntervalKey of Object.keys(this.intervalSettings.timeIntervalGroup)) {
            const intervalIndex = this.intervalSettings.timeIntervalGroup[scheduleTimeIntervalKey].indexOf(interval);
            if (intervalIndex !== -1) {
                return { scheduleTimeIntervalKey, intervalIndex };
            }
        }

        return undefined;
    }

    protected isAuditChecklistSchedule(): boolean {
        return this.fiChecklistHelperService.isAuditSchedule(this.intervalSettings.scheduleType);
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
